// src/utils/redemption.js
// Pure logic for the hybrid-athlete "Rap Sheet": how much a workout pays down
// the debt from last night, and the verdict stamp. No React, no I/O — easy to
// tune and test. Naming locked: The Damage / The Payback / Paid Up · In Debt ·
// Certified Hybrid.

// Debt = 1 point per drink. Each activity pays it down at a different rate,
// tuned so the effort feels roughly fair (and funny). All easily adjustable.
const PAY_RATES = {
  run:      (w) => w.distanceKm || 0,           // 1 drink / km
  hike:     (w) => w.distanceKm || 0,           // like running
  walk:     (w) => (w.distanceKm || 0) / 2,     // 1 / 2 km (partial credit)
  ride:     (w) => (w.distanceKm || 0) / 3,     // biking is easier: 1 / 3 km
  swim:     (w) => (w.distanceKm || 0) * 4,     // brutal: 4 / km
  strength: (w) => (w.durationMin || 0) / 8,    // 1 / 8 min
  hiit:     (w) => (w.durationMin || 0) / 5,    // 1 / 5 min (it's brutal, credit it)
  yoga:     (w) => (w.durationMin || 0) / 20,   // half-rate recovery
  default:  (w) => (w.durationMin || 0) / 12,   // any other logged workout
};

// Verdict thresholds (fraction of debt paid). Tunable.
const PAID_UP_AT = 0.5;   // >= 50% paid → Paid Up
const HYBRID_MIN_DRINKS = 8; // Certified Hybrid needs a real bender too

// Hangover Handicap: fake-serious pace penalty for distance activities.
const HANDICAP_SEC_PER_DRINK = 15; // +15 s/km per drink

const DISTANCE_TYPES = ['run', 'hike', 'walk', 'ride'];

export function computeRedemption(damage, workout) {
  const debt = Math.max(0, Math.round(damage?.drinks || 0));
  const rate = (workout && PAY_RATES[workout.type]) || PAY_RATES.default;
  const paid = workout ? rate(workout) : 0;
  const pct = debt === 0 ? (paid > 0 ? 1 : 1) : paid / debt; // no debt = automatically square
  const fullyPaid = paid >= debt;

  let key, label, sub;
  if (debt === 0) {
    // No drinks → no debt. You can't be In Debt over nothing. (The morning
    // prompt is also skipped for zero-drink sessions — this covers the manual
    // path and any already-saved sheets.)
    if (workout && paid > 0) {
      key = 'paid_up'; label = 'Paid Up'; sub = 'Zero debt and you still sent it. Machine.';
    } else {
      key = 'paid_up'; label = 'All Square'; sub = 'No drinks, no debt. Nothing to prove.';
    }
  } else if (!workout || paid <= 0) {
    key = 'in_debt'; label = 'In Debt'; sub = "No workout detected. The books don't lie.";
  } else if (fullyPaid && debt >= HYBRID_MIN_DRINKS) {
    key = 'certified_hybrid'; label = 'Certified Hybrid'; sub = 'Sent it. Paid it. Absolute unit.';
  } else if (pct >= PAID_UP_AT) {
    key = 'paid_up'; label = 'Paid Up'; sub = fullyPaid ? 'Debt cleared. Go again.' : 'Close enough. We’ll allow it.';
  } else {
    key = 'in_debt'; label = 'In Debt'; sub = 'You started. You did not finish.';
  }

  // Hangover Handicap — only meaningful for distance activities with a pace.
  let handicap = null;
  if (workout && DISTANCE_TYPES.includes(workout.type) && workout.paceSecPerKm) {
    const penalty = HANDICAP_SEC_PER_DRINK * debt;
    handicap = {
      realPaceSec: workout.paceSecPerKm,
      adjPaceSec: workout.paceSecPerKm + penalty,
      penaltySec: penalty,
    };
  }

  return {
    key, label, sub,
    debt,
    paid: Math.round(paid * 10) / 10,
    pct: Math.round(pct * 100) / 100,
    fullyPaid,
    handicap,
  };
}

// Format seconds-per-km as m:ss.
export function fmtPace(sec) {
  if (!sec || !Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Which hero a workout gets on the card: outdoor GPS activities can show a map,
// everything else leads with a centerpiece stat.
export function workoutHasMap(workout) {
  return !!(workout && workout.hasRoute && DISTANCE_TYPES.includes(workout.type));
}

// A friendly label + emoji per activity type for the card.
export const ACTIVITY_META = {
  run:      { emoji: '🏃', label: 'Run' },
  hike:     { emoji: '🥾', label: 'Hike' },
  walk:     { emoji: '🚶', label: 'Walk' },
  ride:     { emoji: '🚴', label: 'Ride' },
  swim:     { emoji: '🏊', label: 'Swim' },
  strength: { emoji: '🏋️', label: 'Strength' },
  hiit:     { emoji: '🔥', label: 'HIIT' },
  yoga:     { emoji: '🧘', label: 'Yoga' },
  default:  { emoji: '💪', label: 'Workout' },
};

export function activityMeta(type) {
  return ACTIVITY_META[type] || ACTIVITY_META.default;
}
